import { Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";

import { Space, Breadcrumb, Menu, Card, Button } from "antd";
import { notification } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { CopyOutlined } from "@ant-design/icons";

import type { Tenant } from "@chirpstack/chirpstack-api-grpc-web/api/tenant_pb";
import { DeleteTenantRequest } from "@chirpstack/chirpstack-api-grpc-web/api/tenant_pb";

import TenantStore from "../../stores/TenantStore";
import DeleteConfirm from "../../components/DeleteConfirm";
import SessionStore from "../../stores/SessionStore";
import Admin from "../../components/Admin";
import EditTenant from "./EditTenant";
import TenantDashboard from "./TenantDashboard";
import TenantIntegrationEvents from "./TenantIntegrationEvents";
import { useTitle } from "../helpers";

// Tenants that are critical integration targets and must never be deleted.
// Mirrors PROTECTED_TENANT_IDS in chirpstack/src/api/tenant.rs — keep in sync.
//   1a13f5c0-013a-45af-b495-c318b96f5de9 = ioX-Connect
//   67b39f69-02ae-4945-84fd-af28ef3a14fc = ioX-Pulse
const PROTECTED_TENANT_IDS = new Set<string>([
  "1a13f5c0-013a-45af-b495-c318b96f5de9",
  "67b39f69-02ae-4945-84fd-af28ef3a14fc",
]);

function TenantLayout({ tenant }: { tenant: Tenant }) {
  useTitle("Tenants", tenant.getName());
  const navigate = useNavigate();
  const location = useLocation();

  const deleteTenant = () => {
    const req = new DeleteTenantRequest();
    req.setId(tenant.getId());

    TenantStore.delete(req, () => {
      SessionStore.setTenantId("");
      navigate("/tenants");
    });
  };

  const copyLoginUrl = async () => {
    const loginUrl = `${window.location.origin}/login?tenantId=${tenant.getId()}`;
    try {
      await navigator.clipboard.writeText(loginUrl);
      notification.success({
        message: "Login URL copied",
        description: loginUrl,
        duration: 3,
      });
    } catch (e) {
      notification.error({
        message: "Copy failed",
        description: "Unable to copy login URL to clipboard.",
        duration: 3,
      });
    }
  };

  let tab = "dashboard";
  if (location.pathname.endsWith("/edit")) {
    tab = "edit";
  }
  if (location.pathname.endsWith("/integration-events")) {
    tab = "integration-events";
  }

  const isProtectedTenant = PROTECTED_TENANT_IDS.has(tenant.getId().toLowerCase());

  const headerExtra = [
    <Admin key="copy-login-url">
      <Button icon={<CopyOutlined />} onClick={copyLoginUrl}>
        Copy login URL
      </Button>
    </Admin>,
  ];
  if (!isProtectedTenant) {
    headerExtra.push(
      <Admin key="delete-tenant">
        <DeleteConfirm confirm={tenant.getName()} typ="tenant" onConfirm={deleteTenant}>
          <Button danger type="primary">
            Delete tenant
          </Button>
        </DeleteConfirm>
      </Admin>,
    );
  }

  return (
    <Space orientation="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => <Breadcrumb items={[{ title: "Tenants" }, { title: tenant.getName() }]} />}
        title={tenant.getName()}
        subTitle={`tenant id: ${tenant.getId()}`}
        extra={headerExtra}
      />

      <Card>
        <Menu
          mode="horizontal"
          selectedKeys={[tab]}
          style={{ marginBottom: 24 }}
          items={[
            { key: "dashboard", label: <Link to={`/tenants/${tenant.getId()}`}>Dashboard</Link> },
            {
              key: "edit",
              label: <Link to={`/tenants/${tenant.getId()}/edit`}>Configuration</Link>,
            },
            {
              key: "integration-events",
              label: <Link to={`/tenants/${tenant.getId()}/integration-events`}>Integration events</Link>,
            },
          ]}
        />
        <Routes>
          <Route path="/" element={<TenantDashboard tenant={tenant} />} />
          <Route path="/edit" element={<EditTenant tenant={tenant} />} />
          <Route path="/integration-events" element={<TenantIntegrationEvents tenant={tenant} />} />
        </Routes>
      </Card>
    </Space>
  );
}

export default TenantLayout;